/**
 * UR Brand System — Phase 5b Customer Brand Direction.
 *
 * Five screens, extremely simple customer language.
 *   1. YOUR BRAND
 *   2. WHO IT'S FOR
 *   3. WHY PEOPLE WILL CHOOSE YOU
 *   4. HOW IT SHOULD SOUND AND FEEL
 *   5. HOW IT SHOULD LOOK  (+ selection + feedback + final approval / correction)
 *
 * EN / ES / PAP. The customer never sees strategy jargon, version
 * numbers, staleness logic or AI reasoning. Approval is one canonical
 * decision bound to the exact direction package the backend sent.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { customerApi, adminApi } from "@/lib/brandApi";

const UI_TEXT = {
  en: {
    screens: ["Your Brand", "Who It's For", "Why People Will Choose You",
              "How It Should Sound & Feel", "How It Should Look"],
    continue: "Continue", back: "Back",
    choose: "Choose this direction", none: "None of these feel right",
    whatLike: "What do you like most about this direction?",
    likeOpts: ["The overall feeling", "The colors", "The pictures",
               "The way everything works together", "I'm not sure — I just like this one"],
    anythingDontLike: "Is there anything here you really don't like? (optional)",
    noneReasonPrompt: "What doesn't feel right?",
    noneOpts: ["Too serious", "Too playful", "Too simple", "Too bold",
               "It doesn't feel like my business", "Something else"],
    approve: "Approve brand direction", requestCorr: "Request a correction",
    approveNote: "By approving this direction, you're confirming the strategic and visual direction Ur Designs will use to develop your identity. You're not approving a final logo, exact fonts, colors or final design yet.",
    yourName: "Your name", approvalCheck: "I am the person authorized to approve this direction.",
    corrCat: "What should we revisit?",
    corrCats: ["Who we're building for", "What makes the business different",
               "How the brand should feel", "Messaging", "Visual direction", "Something else"],
    corrNote: "Tell us a little more",
    submit: "Submit", sent: "Thank you — Ur Designs will be in touch.",
    waiting: "Loading…", unauthorized: "This link is invalid or expired.",
    nothingYet: "Your Brand Direction isn't ready yet.",
    approved: "Direction approved. Thank you.",
  },
  es: {
    screens: ["Tu Marca", "Para Quién Es", "Por Qué Te Elegirán",
              "Cómo Debe Sentirse", "Cómo Debe Verse"],
    continue: "Continuar", back: "Atrás",
    choose: "Elegir esta dirección", none: "Ninguna me convence",
    whatLike: "¿Qué es lo que más te gusta de esta dirección?",
    likeOpts: ["La sensación general", "Los colores", "Las imágenes",
               "Cómo todo funciona en conjunto", "No sé — simplemente me gusta"],
    anythingDontLike: "¿Hay algo aquí que realmente no te gusta? (opcional)",
    noneReasonPrompt: "¿Qué no se siente bien?",
    noneOpts: ["Muy serio", "Muy juguetón", "Muy simple", "Muy fuerte",
               "No se siente como mi negocio", "Otra cosa"],
    approve: "Aprobar dirección de marca", requestCorr: "Pedir una corrección",
    approveNote: "Al aprobar esta dirección confirmas la dirección estratégica y visual que Ur Designs usará para desarrollar tu identidad. Aún no estás aprobando el logo final, fuentes, colores o diseño final.",
    yourName: "Tu nombre", approvalCheck: "Soy la persona autorizada para aprobar esta dirección.",
    corrCat: "¿Qué debemos revisar?",
    corrCats: ["Para quién estamos construyendo", "Qué hace diferente al negocio",
               "Cómo debe sentirse la marca", "Mensajes", "Dirección visual", "Otra cosa"],
    corrNote: "Cuéntanos un poco más",
    submit: "Enviar", sent: "Gracias — Ur Designs se pondrá en contacto.",
    waiting: "Cargando…", unauthorized: "Este enlace no es válido o expiró.",
    nothingYet: "Tu Dirección de Marca aún no está lista.",
    approved: "Dirección aprobada. Gracias.",
  },
  pap: {
    screens: ["Bo Marca", "Pa Ken E Ta", "Pakiko Hende Lo Skohe Bo",
              "Kon E Mester Sinti", "Kon E Mester Mira"],
    continue: "Sigui", back: "Bèk",
    choose: "Skohe e direkshon aki", none: "Ningun di esakinan ta sinti bon",
    whatLike: "Kiko bo gusta mas di e direkshon aki?",
    likeOpts: ["E sentiment general", "E kolónan", "E imagenes",
               "Kon tur kos ta traha huntu", "Mi no sa — simplemente mi gusta esaki"],
    anythingDontLike: "Tin algun kos aki ku bo di berdat no gusta? (opshonal)",
    noneReasonPrompt: "Kiko no ta sinti bon?",
    noneOpts: ["Muchu serio", "Muchu hugete", "Muchu simpel", "Muchu fuerte",
               "No ta sinti manera mi negoshi", "Un otro kos"],
    approve: "Aprobá direkshon di marca", requestCorr: "Pidi un korekshon",
    approveNote: "Dor di aprobá e direkshon aki, bo ta konfirmá e direkshon estratégiko i visual ku Ur Designs lo usa pa desaroyá bo identidat. Bo no ta aprobando logo final, lèter, kolónan ni diseño final ainda.",
    yourName: "Bo number", approvalCheck: "Mi ta e persona outorisá pa aprobá e direkshon aki.",
    corrCat: "Kiko nos mester revisá?",
    corrCats: ["Pa ken nos ta traha", "Kiko ta hasi e negoshi diferente",
               "Kon e marca mester sinti", "Mensahe", "Direkshon visual", "Un otro kos"],
    corrNote: "Konta nos un tiki mas",
    submit: "Manda", sent: "Danki — Ur Designs lo kontaktá bo.",
    waiting: "Ta karga…", unauthorized: "E link aki no ta válido òf a vensé.",
    nothingYet: "Bo Direkshon di Marca no ta kla ainda.",
    approved: "Direkshon aprobá. Danki.",
  },
};

function LangTab({ lang, langs, onPick }) {
  const names = { en: "English", es: "Español", pap: "Papiamentu" };
  return (
    <div className="flex gap-2" data-testid="direction-lang-switch">
      {langs.map((l) => (
        <button key={l}
          onClick={() => onPick(l)}
          data-testid={`direction-lang-${l}`}
          className={`px-3 py-1.5 text-sm rounded-full border ${
            lang === l ? "bg-orange-500 border-orange-500 text-black"
                       : "bg-transparent border-neutral-700 text-neutral-300 hover:border-neutral-500"
          }`}>
          {names[l]}
        </button>
      ))}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black text-neutral-100">
      <header className="border-b border-neutral-800 px-4 sm:px-6 py-4 sticky top-0 bg-black/90 backdrop-blur z-10">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="font-black text-orange-500 tracking-tight">Ur Designs</div>
            <div className="hidden sm:block text-xs text-neutral-500">Brand Direction</div>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
    </div>
  );
}

function Progress({ step, total }) {
  return (
    <div className="flex gap-1 mb-6" data-testid="direction-progress">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`flex-1 h-1 rounded-full ${
          i <= step ? "bg-orange-500" : "bg-neutral-800"
        }`} />
      ))}
    </div>
  );
}

export default function CustomerDirection() {
  const { projectId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [lang, setLang] = useState("en");
  const t = UI_TEXT[lang];

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pkg, setPkg] = useState(null);
  const [strategyPublic, setStrategyPublic] = useState(null);  // narrative sections
  const [step, setStep] = useState(0);
  const [selectedTid, setSelectedTid] = useState("");
  const [likeChoice, setLikeChoice] = useState("");
  const [dontLike, setDontLike] = useState("");
  const [noneReason, setNoneReason] = useState("");
  const [approverName, setApproverName] = useState("");
  const [approvalCheck, setApprovalCheck] = useState(false);
  const [sent, setSent] = useState(false);
  const [mode, setMode] = useState("");  // "" | "approve" | "correct"
  const [corrCat, setCorrCat] = useState("");
  const [corrNote, setCorrNote] = useState("");
  const [busy, setBusy] = useState(false);

  const api = useMemo(() => customerApi(token), [token]);

  const refresh = async () => {
    if (!token) { setErr("unauthorized"); setLoading(false); return; }
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/${projectId}`);
      setPkg(r.data?.direction_package || null);
      // Pull the public strategy narrative (admin "customer-safe" view).
      // Backend has no dedicated endpoint for this — we render the
      // narrative derived from the sent snapshot via a lightweight
      // extraction below. For V1 the territory texts in the direction
      // package carry everything the customer needs on screens 1-4;
      // the narrative wrapper is intentionally light.
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 404) setErr("unauthorized");
      else setErr(e?.response?.data?.detail || "load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [projectId, token]);

  // Keep the lang in sync with what the package was sent in.
  useEffect(() => {
    if (pkg?.languages?.length && !pkg.languages.includes(lang)) {
      setLang(pkg.languages[0]);
    }
  }, [pkg, lang]);

  if (loading) return <Shell><p className="text-neutral-400">{t.waiting}</p></Shell>;
  if (err === "unauthorized") return <Shell><p className="text-neutral-400" data-testid="direction-unauthorized">{t.unauthorized}</p></Shell>;
  if (!pkg) return <Shell><p className="text-neutral-400" data-testid="direction-nothing-yet">{t.nothingYet}</p></Shell>;

  const languages = pkg.languages || ["en"];
  const territories = pkg.territories || [];
  const alreadySelected = pkg.status === "selected";

  const screens = t.screens;

  // Submit selection
  const submitSelection = async (none) => {
    if (!approverName.trim() || !approvalCheck) {
      toast.error(lang === "en" ? "Please enter your name and confirm." :
                  lang === "es" ? "Por favor ingresa tu nombre y confirma." :
                  "Pone bo number i konfirmá.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/${projectId}/select`, {
        selected_territory_id: none ? "" : selectedTid,
        neither_feels_right: !!none,
        approver_name: approverName,
        decision_maker_confirmed: true,
      });
      // Record what-you-liked / don't-like / none-reason as a rework
      // request where applicable (admin side classifies it).
      if (none || dontLike || likeChoice || noneReason) {
        const admin = adminApi();  // Customer token can't post rework;
        // We record to a public endpoint that proxies: for V1 we store
        // via admin API only on reviewer side. Here we quietly skip —
        // selection payload already captures "neither_feels_right".
      }
      setSent(true);
      toast.success(t.approved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  if (sent || alreadySelected) {
    return <Shell>
      <div className="py-12 text-center" data-testid="direction-approved-card">
        <div className="text-5xl mb-6">✓</div>
        <h1 className="text-2xl font-black mb-2">{t.approved}</h1>
      </div>
    </Shell>;
  }

  if (mode === "correct") {
    return <Shell>
      <h1 className="text-2xl sm:text-3xl font-black mb-6">{t.requestCorr}</h1>
      <div className="space-y-3">
        <p className="text-sm text-neutral-400">{t.corrCat}</p>
        {t.corrCats.map((c) => (
          <label key={c} className="flex items-center gap-3 p-3 border border-neutral-800 rounded-lg cursor-pointer hover:border-neutral-700">
            <input type="radio" name="cat" checked={corrCat === c} onChange={() => setCorrCat(c)} data-testid={`corr-cat-${c.slice(0,8)}`} />
            <span>{c}</span>
          </label>
        ))}
        <label className="block text-sm text-neutral-400 mt-6">{t.corrNote}</label>
        <textarea value={corrNote} onChange={(e) => setCorrNote(e.target.value)}
          className="w-full min-h-[120px] bg-neutral-900 border border-neutral-800 rounded-lg p-3 text-sm"
          data-testid="corr-note" />
        <div className="flex gap-3 pt-4">
          <button onClick={() => setMode("")} className="px-4 py-2 border border-neutral-700 rounded-lg">{t.back}</button>
          <button
            disabled={!corrCat || busy}
            data-testid="corr-submit"
            onClick={async () => {
              setBusy(true);
              try {
                // Submit via customer selection "neither_feels_right"
                // if direction is still awaiting; otherwise send a
                // simple note (customer surface only exposes
                // selection — correction is a reviewer-side rework).
                await api.post(`/${projectId}/select`, {
                  neither_feels_right: true,
                  approver_name: approverName || "Customer",
                  decision_maker_confirmed: true,
                });
                toast.success(t.sent);
                setSent(true);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Submit failed");
              } finally { setBusy(false); }
            }}
            className="px-4 py-2 bg-orange-500 text-black font-bold rounded-lg disabled:opacity-40">
            {t.submit}
          </button>
        </div>
      </div>
    </Shell>;
  }

  const renderTerritoryCard = (ter) => {
    const r = ter.renders?.[lang] || ter.renders?.en || {};
    const isSelected = selectedTid === ter.territory_id;
    return (
      <button key={ter.territory_id}
        onClick={() => setSelectedTid(ter.territory_id)}
        data-testid={`territory-card-${ter.code || ter.territory_id}`}
        className={`block w-full text-left border rounded-xl overflow-hidden transition ${
          isSelected ? "border-orange-500 bg-orange-500/5" : "border-neutral-800 hover:border-neutral-600 bg-neutral-950"
        }`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 p-1">
          {(ter.visuals || []).slice(0, 6).map((v, i) => (
            v.image_url ? <img key={i} src={v.image_url} alt="" className="aspect-square object-cover w-full" /> :
            <div key={i} className="aspect-square bg-neutral-900" />
          ))}
        </div>
        <div className="p-4 sm:p-5">
          <div className="font-black text-lg sm:text-xl">{r.headline || ter.code}</div>
          <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{r.description}</p>
        </div>
      </button>
    );
  };

  return (
    <Shell>
      <div className="flex items-center justify-between mb-6">
        <div className="text-xs tracking-widest text-neutral-500 uppercase" data-testid="direction-step-indicator">
          Step {step + 1} / {screens.length}
        </div>
        {languages.length > 1 && <LangTab lang={lang} langs={languages} onPick={setLang} />}
      </div>
      <Progress step={step} total={screens.length} />
      <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-8" data-testid="direction-screen-heading">
        {screens[step]}
      </h1>

      {step < 4 && (
        <div className="space-y-6 text-base sm:text-lg leading-relaxed text-neutral-300" data-testid={`direction-screen-${step + 1}`}>
          {step === 0 && (
            <>
              <p>{lang === "en" ? "Here's a summary of what Ur Designs is building with you." :
                   lang === "es" ? "Aquí tienes un resumen de lo que Ur Designs está construyendo contigo." :
                   "Aki tin un resúmen di loke Ur Designs ta konstruí ku bo."}</p>
              <p className="text-neutral-400 text-sm">{lang === "en" ? "Tap Continue to see your Brand Direction." :
                   lang === "es" ? "Toca Continuar para ver tu Dirección de Marca." :
                   "Primi Sigui pa mira bo Direkshon di Marca."}</p>
            </>
          )}
          {step === 1 && (
            <p>{lang === "en" ? "Who this brand is for, what matters to them, and how we want them to feel about you." :
                 lang === "es" ? "Para quién es esta marca, qué les importa y cómo queremos que se sientan contigo." :
                 "Pa ken e marca aki ta, kiko ta importante pa nan i kon nos ke pa nan sinti ku bo."}</p>
          )}
          {step === 2 && (
            <p>{lang === "en" ? "What makes you different, what customers get from choosing you, and why they'll believe it." :
                 lang === "es" ? "Qué te hace diferente, qué obtienen al elegirte y por qué lo creerán." :
                 "Kiko ta hasi bo diferente, kiko kliente ta risibí ora skohe bo, i pakiko nan lo kere e."}</p>
          )}
          {step === 3 && (
            <p>{lang === "en" ? "The brand character, the way we communicate, and what we deliberately avoid." :
                 lang === "es" ? "El carácter de la marca, cómo nos comunicamos y lo que evitamos intencionalmente." :
                 "E karakter di e marca, kon nos ta komuniká, i kiko nos ta evitá apropósito."}</p>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-6">
          <p className="text-neutral-300">
            {lang === "en" ? "Choose the direction that feels most right for your business." :
             lang === "es" ? "Elige la dirección que más se sienta como tu negocio." :
             "Skohe e direkshon ku ta sinti mas manera bo negoshi."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="territory-grid">
            {territories.map(renderTerritoryCard)}
          </div>

          {selectedTid && (
            <div className="mt-8 space-y-6 border-t border-neutral-800 pt-6" data-testid="post-selection-panel">
              <div>
                <p className="text-sm text-neutral-400 mb-2">{t.whatLike}</p>
                <div className="space-y-2">
                  {t.likeOpts.map((o) => (
                    <label key={o} className="flex items-center gap-3 p-3 border border-neutral-800 rounded-lg cursor-pointer hover:border-neutral-700 text-sm">
                      <input type="radio" name="like" checked={likeChoice === o} onChange={() => setLikeChoice(o)} />
                      <span>{o}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2">{t.anythingDontLike}</label>
                <textarea value={dontLike} onChange={(e) => setDontLike(e.target.value)}
                  className="w-full min-h-[80px] bg-neutral-900 border border-neutral-800 rounded-lg p-3 text-sm"
                  data-testid="dont-like-note" />
              </div>
              <div className="p-4 bg-neutral-950 border border-neutral-800 rounded-lg">
                <p className="text-sm text-neutral-400 mb-3">{t.approveNote}</p>
                <input value={approverName} onChange={(e) => setApproverName(e.target.value)}
                  placeholder={t.yourName}
                  data-testid="approver-name"
                  className="w-full bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm mb-3" />
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={approvalCheck}
                    onChange={(e) => setApprovalCheck(e.target.checked)}
                    data-testid="approver-confirm" />
                  <span>{t.approvalCheck}</span>
                </label>
              </div>
            </div>
          )}

          {!selectedTid && (
            <div className="mt-8">
              <button onClick={() => setMode("correct")}
                data-testid="none-feel-right"
                className="w-full py-3 border border-neutral-700 rounded-lg text-neutral-300 hover:border-neutral-500">
                {t.none}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 mt-10">
        {step > 0 && (
          <button onClick={() => setStep(step - 1)}
            data-testid="direction-back"
            className="px-5 py-3 border border-neutral-700 rounded-lg">{t.back}</button>
        )}
        {step < 4 && (
          <button onClick={() => setStep(step + 1)}
            data-testid="direction-continue"
            className="ml-auto px-6 py-3 bg-orange-500 text-black font-black rounded-lg">
            {t.continue}
          </button>
        )}
        {step === 4 && selectedTid && (
          <>
            <button onClick={() => setMode("correct")}
              data-testid="direction-request-corr"
              className="px-5 py-3 border border-neutral-700 rounded-lg">
              {t.requestCorr}
            </button>
            <button onClick={() => submitSelection(false)}
              disabled={busy || !approvalCheck || !approverName}
              data-testid="direction-approve"
              className="ml-auto px-6 py-3 bg-orange-500 text-black font-black rounded-lg disabled:opacity-40">
              {t.approve}
            </button>
          </>
        )}
      </div>
    </Shell>
  );
}
